/**
 * index.js — bootstrap.
 *
 * Contract with every host portal: this script is allowed to append one element
 * and nothing more. Any failure — no network, bad JSON, missing asset, blocked
 * storage — must end as a silent no-op, never as a broken page.
 */

import { CONFIG_URL, RECHECK_MS, PARAMS, GLOBAL_KEY, ROOT_ID, VERSION } from './config.js';
import { resolveActive } from './schedule.js';
import { getSnoozeUntil, setSnooze, clearSnooze } from './storage.js';
import { mount } from './render.js';

// Double-injection guard: a portal that ships the tag twice gets one banner.
if (!window[GLOBAL_KEY] && !document.getElementById(ROOT_ID)) {
  const api = { version: VERSION };
  window[GLOBAL_KEY] = api;

  const flags = readFlags();
  const log = flags.debug
    ? function () { console.log.apply(console, ['[2c-events]'].concat([].slice.call(arguments))); }
    : function () {};

  let config = null;
  let view = null;
  let showing = null;

  function readFlags() {
    let params;
    try {
      params = new URLSearchParams(location.search);
    } catch (e) {
      params = { get: function () { return null; } };
    }
    return {
      date: params.get(PARAMS.date) || window.__2C_EVENTS_DATE__ || null,
      debug: params.get(PARAMS.debug) === '1',
      reset: params.get(PARAMS.reset) === '1',
    };
  }

  /** QA can pretend it is any date; production always uses the real clock. */
  function now() {
    const m = flags.date && /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(flags.date).trim());
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date();
  }

  function teardown() {
    if (view) view.destroy();
    view = null;
    showing = null;
  }

  function evaluate() {
    const ev = resolveActive(config, now(), location.hostname);

    if (!ev) {
      if (showing) log('no active event, removing', showing);
      teardown();
      return;
    }

    const snoozedUntil = getSnoozeUntil(ev.id);
    if (snoozedUntil > Date.now()) {
      log('snoozed until', new Date(snoozedUntil).toISOString(), ev.id);
      teardown();
      return;
    }

    if (showing === ev.id && view) return; // already on screen, leave it alone

    teardown();
    showing = ev.id;
    log('showing', ev.id, ev);
    view = mount(ev, function () {
      const until = setSnooze(ev.id, ev.snoozeHours);
      log('dismissed until', new Date(until).toISOString());
      showing = null;
      view = null;
    });
  }

  function start() {
    fetch(CONFIG_URL, { credentials: 'omit', mode: 'cors' })
      .then(function (res) {
        if (!res.ok) throw new Error('calendar HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        config = json;
        log('calendar loaded', config);

        if (flags.reset) {
          (config.events || []).forEach(function (e) { if (e && e.id) clearSnooze(e.id); });
          log('all snoozes cleared');
        }

        evaluate();
        // Long-lived tabs and SPA sessions pick up midnight rollover.
        setInterval(function () { guard(evaluate); }, RECHECK_MS);

        if (flags.debug) {
          api.config = config;
          api.evaluate = function () { guard(evaluate); };
          api.reset = function () {
            (config.events || []).forEach(function (e) { if (e && e.id) clearSnooze(e.id); });
            guard(evaluate);
          };
        }
      })
      .catch(function (err) { log('inactive:', err && err.message); });
  }

  /** Nothing this script does is worth breaking a host page over. */
  function guard(fn) {
    try { fn(); } catch (err) { log('error:', err); }
  }

  // `defer`, `async` and GTM tags all run after DOMContentLoaded has fired —
  // binding to that event alone is why the old script silently did nothing.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { guard(start); }, { once: true });
  } else {
    guard(start);
  }
}
